import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useFormContext, Controller } from "react-hook-form";
import {
  Box,
  Typography,
  FormControl,
  FormLabel,
  Divider,
  TextField,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  FormHelperText,
  Checkbox,
  FormControlLabel,
} from "@mui/material";

import MultiChipCheck from "../../../../../../shared/Form/MultiChipCheck";
import { MediaUploadField } from "../../../../../../shared/Form/MediaUploadField";

import { CreateProgramFormData } from "../../../../types/service.type";
import { useGetProgramEquipments } from "../../../../api/queries/useGetProgramEquipments";
import { CloseVideoAccordionIcon, OpenVideoAccordionIcon } from "../../../../assets/icons/create-service-icons";
import { TermsAndConditionsModal, useTermsAndConditionsModal } from "../../../../components/TermsAndConditionsModal";

const ProgramContentAndTerms = () => {
  const { t } = useTranslation("goService", { keyPrefix: "createServicePage.programContentAndTermsStep" });
  const { open, handleOpen, handleClose } = useTermsAndConditionsModal();
  const {
    control,
    formState: { errors },
    watch,
  } = useFormContext<CreateProgramFormData>();

  const numberOfVideos = watch("numberOfVideos");
  const idealCompletionTime = watch("idealCompletionTime");

  return (
    <Box sx={styles.container}>
      <Typography sx={styles.formLabel}>{t("yourProgramContentDetails")}</Typography>
      <Box sx={styles.programContentBox}>
        <Box sx={styles.programContentWrapper}>
          <Typography sx={styles.programContentLabel}>{t("ideallyThisProgramIsFinishedIn")}</Typography>
          <Typography sx={styles.programContentValue}>{t("monthsNumber", { count: idealCompletionTime })}</Typography>
        </Box>
        <Box sx={styles.programContentWrapper}>
          <Typography sx={styles.programContentLabel}>{t("totalNumberVideos")}</Typography>
          <Typography sx={styles.programContentValue}>{t("videoCount", { count: numberOfVideos })}</Typography>
        </Box>

        {Array.from({ length: numberOfVideos }, (_, index) => (
          <VideoContentAccordion key={index} videoNumber={index + 1} />
        ))}

        <Divider flexItem />

        <Box sx={styles.offerFreeContainer}>
          <Box sx={styles.offerFreeContentContainer}>
            <Typography sx={styles.offerFreeTitle}>{t("offerOneVideoForFree")}</Typography>
            <Typography sx={styles.offerFreeDescription}>{t("selectOneVideo")}</Typography>
          </Box>
          <Box sx={styles.offerFreeLabelsContainer}>
            {Array.from({ length: numberOfVideos }, (_, idx) => (
              <Controller
                name={`programContents.${idx}.isFree`}
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <FormControlLabel
                    sx={styles.formControlLabelWithCheckbox}
                    control={
                      <Checkbox
                        size="small"
                        checked={field.value ?? false}
                        onChange={(e) => {
                          field.onChange(e.target.checked);
                        }}
                      />
                    }
                    label={t("videoCount", { count: idx + 1 })}
                  />
                )}
              />
            ))}
          </Box>
        </Box>

        <Divider flexItem />
        <Typography sx={styles.formLabel}>{t("termsAndCondetion")}</Typography>
        <Controller
          name="allowGoalngoMemebers"
          control={control}
          render={({ field, fieldState: { error } }) => (
            <>
              <FormControlLabel
                sx={styles.formControlLabel}
                control={
                  <Checkbox
                    size="small"
                    checked={field.value ?? false}
                    onChange={(e) => field.onChange(e.target.checked)}
                  />
                }
                label={<FormLabel sx={styles.formControlLabel}>{t("allowGoalngoMemebers")}</FormLabel>}
              />
            </>
          )}
        />
        <Controller
          name="agreeToTerms"
          control={control}
          render={({ field, fieldState: { error } }) => (
            <FormControlLabel
              sx={styles.formControlLabel}
              control={
                <Checkbox
                  size="small"
                  checked={field.value ?? false}
                  onChange={(e) => field.onChange(e.target.checked)}
                />
              }
              label={
                <>
                  <FormLabel sx={{ ...styles.formControlLabel, display: "flex", gap: "4px" }} error={!!error}>
                    {t("iAgreeToGoalngo")}
                    <Typography
                      sx={{
                        ...styles.formControlLabel,
                        color: "#106048",
                        textDecoration: "underline",
                        cursor: "pointer",
                      }}
                      onClick={handleOpen}
                    >
                      {t("termsAndConditions")}
                    </Typography>
                  </FormLabel>
                  {error?.message && <FormHelperText error>{t(error.message)}</FormHelperText>}
                </>
              }
            />
          )}
        />
      </Box>
      <TermsAndConditionsModal open={open} closeModal={handleClose} />
    </Box>
  );
};

export default ProgramContentAndTerms;

const VideoContentAccordion = ({ videoNumber }: { videoNumber: number }) => {
  const { t } = useTranslation("goService", { keyPrefix: "createServicePage.programContentAndTermsStep" });
  const { t: tEquipments } = useTranslation("goService", { keyPrefix: "equipments" });

  const [isOpen, setIsOpen] = useState(false);
  const handleToggleVideoAccordion = () => setIsOpen((prev) => !prev);
  const { data: programEquipments, isLoading: isProgramEquipmentsLoading } = useGetProgramEquipments();

  const { control, setValue } = useFormContext<CreateProgramFormData>();
  return (
    <Accordion sx={styles.videoAccordion} onChange={handleToggleVideoAccordion}>
      <AccordionSummary
        sx={styles.videoAccordionSummary}
        expandIcon={isOpen ? <CloseVideoAccordionIcon /> : <OpenVideoAccordionIcon />}
      >
        {t("videoNumber", { number: videoNumber })}
      </AccordionSummary>
      <AccordionDetails sx={styles.videoAccordionDetails}>
        <Box sx={styles.videoAccordionEquipmentsContainer}>
          <Controller
            name={`programContents.${videoNumber - 1}.media`}
            control={control}
            render={({ field, fieldState: { error } }) => (
              <FormControl fullWidth component="fieldset" error={!!error}>
                <MediaUploadField
                  value={field.value}
                  onChange={(value) => {
                    if (Array.isArray(value)) return;
                    field.onChange(value);
                    setValue(`programContents.${videoNumber - 1}.duration`, value?.duration ?? 0);
                  }}
                  onRemove={() => field.onChange(null)}
                />
                {error?.message && <FormHelperText>{t(error.message)}</FormHelperText>}
              </FormControl>
            )}
          />
          <Controller
            name={`programContents.${videoNumber - 1}.videoTitle`}
            control={control}
            render={({ field, fieldState: { error } }) => (
              <FormControl fullWidth component="fieldset" error={!!error}>
                <FormLabel sx={styles.formChildLabel}>{t("videoTitle")}</FormLabel>
                <TextField
                  {...field}
                  fullWidth
                  placeholder={t("writeVideoTitle")}
                  error={!!error}
                  helperText={error?.message && t(error.message)}
                />
              </FormControl>
            )}
          />
          <Controller
            name={`programContents.${videoNumber - 1}.instructions`}
            control={control}
            render={({ field, fieldState: { error } }) => (
              <FormControl fullWidth component="fieldset" error={!!error}>
                <FormLabel sx={styles.formChildLabel}>{t("describeYourProgram")}</FormLabel>
                <TextField
                  {...field}
                  fullWidth
                  placeholder={t("eGiP")}
                  multiline
                  rows={5}
                  error={!!error}
                  helperText={error?.message && t(error.message)}
                />
              </FormControl>
            )}
          />
          <Controller
            name={`programContents.${videoNumber - 1}.equipments`}
            control={control}
            defaultValue={[]}
            render={({ field }) => (
              <>
                {programEquipments?.map(({ category, equipments }) => {
                  const selectedEquipmentsCount =
                    field.value?.filter((id) => equipments.some((e) => Number(e.id) === id)).length || 0;

                  return (
                    <FormControl key={category} sx={styles.formControl}>
                      <FormLabel sx={styles.formChildLabelWithChip}>
                        {tEquipments(category)}
                        <Chip
                          label={selectedEquipmentsCount}
                          sx={styles.equipmentsCountChip(selectedEquipmentsCount === 0)}
                        />
                      </FormLabel>
                      <Box sx={{ display: "flex", flexWrap: "wrap", gap: "12px" }}>
                        {equipments.map(({ id, title }) => {
                          const isSelected = field.value?.includes(Number(id)) ?? false;
                          return (
                            <MultiChipCheck
                              key={id}
                              label={title}
                              isSelected={isSelected}
                              handleOnClick={() => {
                                const newEquipments = isSelected
                                  ? (field.value || []).filter((d) => d !== Number(id))
                                  : [...(field.value || []), Number(id)];

                                field.onChange(newEquipments);
                              }}
                            />
                          );
                        })}
                      </Box>
                      <Divider />
                    </FormControl>
                  );
                })}
              </>
            )}
          />
        </Box>
      </AccordionDetails>
    </Accordion>
  );
};

const styles = {
  container: {
    display: "flex",
    flexDirection: "column",
    gap: "16px",
  },
  serviceDeliveryRadioGroup: {
    display: "flex",
    flexDirection: "row",
    gap: "16px",
  },
  formLabel: {
    fontFamily: "Montserrat",
    color: "var(--Dark---1000, #111)",
    fontSize: "18px",
    fontStyle: "normal",
    fontWeight: 600,
    lineHeight: "28px",
  },
  programContentBox: {
    display: "flex",
    padding: "16px",
    flexDirection: "column",
    alignItems: "flex-start",
    gap: "12px",
    borderRadius: "12px",
    border: "1px solid var(--Gray---100, #E7E7E7)",
    background: "var(--Base-White, #FFF)",
  },
  programContentWrapper: {
    width: "100%",
    display: "flex",
    justifyContent: "space-between",
  },
  programContentLabel: {
    fontFamily: "Montserrat",
    color: "var(--Dark---600, #707070)",
    fontSize: "12px",
    fontStyle: "normal",
    fontWeight: 600,
    lineHeight: "18px",
  },
  programContentValue: {
    color: "var(--black-100, #1C1C1C)",
    fontFamily: "Montserrat",
    fontSize: "12px",
    fontStyle: "normal",
    fontWeight: 700,
    lineHeight: "18px",
  },
  formChildLabel: {
    color: "var(--Dark---800, #414141)",
    fontFamily: "Montserrat",
    fontSize: "16px",
    fontStyle: "normal",
    fontWeight: 600,
    lineHeight: "24px",
  },
  formControl: {
    display: "flex",
    flexDirection: "column",
    gap: "16px",
  },
  videoAccordion: {
    borderRadius: "16px",
    background: "var(--Gray-50, #F9FAFB)",
    padding: "20px",
    width: "100%",
    "&:before": {
      display: "none",
    },
    boxShadow: "none",
  },
  videoAccordionSummary: {
    fontFamily: "Montserrat",
    color: "var(--Gray-900, #101828)",
    fontSize: "20px",
    fontStyle: "normal",
    fontWeight: 500,
    lineHeight: "30px",
    padding: 0,
    height: "fit-content",
    minHeight: "unset",
    "& .MuiAccordionSummary-content": {
      margin: 0,
    },
  },
  videoAccordionDetails: {
    display: "flex",
    flexDirection: "column",
    gap: "16px",
    padding: 0,
  },
  videoAccordionEquipmentsContainer: {
    display: "flex",
    flexDirection: "column",
    gap: "16px",
    borderRadius: "12px",
    border: "1px solid var(--Gray---100, #E7E7E7)",
    background: "#FFF",
    padding: "16px",
  },
  equipmentsCountChip: (isZero: boolean) => ({
    textAlign: "center",
    fontSize: "10px",
    fontStyle: "normal",
    fontWeight: 700,
    lineHeight: "normal",
    padding: "1px 5px",
    background: isZero ? "var(--Gray---400, #A0A0A0)" : "var(--Green---100, #008B07)",
    color: "#FFF",
    minHeight: "0",
    height: "fit-content",
    "& .MuiChip-label": {
      padding: "0",
    },
  }),
  formChildLabelWithChip: {
    color: "var(--Dark---800, #414141)",
    fontFamily: "Montserrat",
    fontSize: "16px",
    fontStyle: "normal",
    fontWeight: 600,
    lineHeight: "24px",
    display: "flex",
    alignItems: "center",
    gap: "4px",
  },
  formControlLabel: {
    margin: 0,
    fontFamily: "Montserrat",
    color: "var(--Dark---1000, #111)",
    fontSize: "16px",
    fontStyle: "normal",
    fontWeight: 500,
    lineHeight: "24px",
  },
  formControlLabelWithCheckbox: {
    margin: 0,
    fontFamily: "Montserrat",
    color: "var(--Dark---1000, #111)",
    fontSize: "16px",
    fontStyle: "normal",
    fontWeight: 500,
    lineHeight: "24px",

    width: "calc(50% - 4px)",
    padding: "12px 14px",
    alignItems: "center",
    gap: "8px",
    alignSelf: "stretch",
    borderRadius: "8px",
    border: "1px solid var(--Gray-300, #D0D5DD)",
    background: "var(--Base-White, #FFF)",
    boxShadow: "0 1px 2px 0 rgba(16, 24, 40, 0.05)",
  },
  offerFreeContainer: {
    width: "100%",
    display: "flex",
    padding: "16px",
    flexDirection: "column",
    alignItems: "flex-start",
    gap: "16px",
    borderRadius: "12px",
    border: "1px solid var(--Gray-200, #EAECF0)",
    background: "var(--Base-White, #FFF)",
  },
  offerFreeContentContainer: {
    display: "flex",
    flexDirection: "column",
    gap: "4px",
  },
  offerFreeTitle: {
    color: "var(--black-100, #1C1C1C)",
    fontFamily: "Montserrat",
    fontSize: "16px",
    fontStyle: "normal",
    fontWeight: 600,
    lineHeight: "24px",
  },
  offerFreeDescription: {
    fontFamily: "Montserrat",
    color: "var(--Dark---600, #707070)",
    fontSize: "12px",
    fontStyle: "normal",
    fontWeight: 500,
    lineHeight: "18px",
  },
  offerFreeLabelsContainer: {
    display: "flex",
    gap: "8px",
    flexWrap: "wrap",
    width: "100%",
  },
};
